/* @flow */
import URI from 'urijs';
import cx from 'classnames';
import * as React from 'react';

import { mountModal } from 'lib/modal';
import ChatStore from 'chat/stores/ChatStore';
import CurrentUser from 'auth/CurrentUser';
import Link from 'components/Link';
import PhoneCallMessageItemNote from 'chat/components/PhoneCallMessageItemNote';
import PhoneCallModal, { isFixableDenialReason } from 'phoneCalls/components/PhoneCallModal';
import Text from 'components/Text';
import Time from 'components/Time';
import UserAvatar from 'users/components/UserAvatar';

import './styles/MessageItem.scss';

function parseCallToActionLink(callToAction) {
  if (!callToAction) {
    return undefined;
  }

  const uri = URI(callToAction.link);

  if (uri.host() === 'call') {
    return {
      action: 'call',
      calleeUuid: uri.query(true).callee_uuid,
    };
  }

  return undefined;
}

function formatDuration(duration: number): ?string {
  if (!duration || duration <= 0) {
    return undefined;
  }

  const sec = duration % 60;
  const min = Math.floor(duration / 60);

  if (min && sec) {
    return `${min}m ${sec}s`;
  } else if (min) {
    return `${min}m`;
  } else if (sec) {
    return `${sec}s`;
  } else {
    return undefined;
  }
}

type Props = {
  message: PhoneCallMessageGQL,
  lastOfGroup: boolean,
};

type State = {
  hovered: boolean,
};

class PhoneCallMessageItem extends React.Component<Props, State> {
  state: State = {
    hovered: false,
  };

  isAuthor() {
    return CurrentUser.getUserUuid() === this.props.message.sender.uuid;
  }

  handleEnterHover = () => {
    this.setState({ hovered: true });
  };

  handleLeaveHover = () => {
    this.setState({ hovered: false });
  };

  shouldRenderCTA = (): boolean => {
    const { message } = this.props;
    const cta = parseCallToActionLink(message.callToAction);

    if (!cta) {
      return false;
    }

    if (cta.action === 'call') {
      const chatFromMessage = message.chat;
      if (!chatFromMessage) {
        return false;
      }
      const chat = ChatStore.getChat(chatFromMessage.uuid);
      return !!chat && (chat.canPhone() || isFixableDenialReason(chat.getCanPhoneDenialReason()));
    }

    return true;
  };

  renderHover() {
    const { message } = this.props;

    return (
      <div className="MessageItem-hover">
        <div className="MessageItem-hover-receipt">
          <Time date={new Date(message.createdAt)} hour="numeric" minute="2-digit" />
        </div>
      </div>
    );
  }

  renderCTA() {
    const { callToAction } = this.props.message;
    const link = parseCallToActionLink(callToAction);

    if (!(callToAction && link)) {
      return null;
    }

    if (link.action !== 'call') {
      console.error(`phone call message item: unknown action ${link.action}`);
      return null;
    }

    const onClick = () => mountModal(<PhoneCallModal calleeUuid={link.calleeUuid} />, this);

    return (
      <div className="MessageItem-phoneCallCTA">
        <Link onClick={onClick}>
          <Text size="small" align="center">
            {callToAction.text}
          </Text>
        </Link>
      </div>
    );
  }

  renderBody() {
    const { body } = this.props.message;
    const { duration, status, note, reasons } = this.props.message.phoneCall;
    const formattedDuration = formatDuration(duration);

    const color = status === 'completed' ? 'pitch' : 'lava';
    return (
      <div>
        <div className="MessageItemContent-body">
          <Text color={color}>{body}</Text>
          {formattedDuration && (
            <div>
              <Text color="thunder" size="xsmall">
                {formattedDuration}
              </Text>
            </div>
          )}
        </div>
        {this.shouldRenderCTA() && !note && !reasons.length ? this.renderCTA() : this.renderNote()}
      </div>
    );
  }

  renderNote() {
    return <PhoneCallMessageItemNote uuid={this.props.message.phoneCall.uuid} />;
  }

  renderDroplet() {
    const { lastOfGroup, message } = this.props;

    return lastOfGroup && !this.isAuthor() ? (
      <span className="MessageItem-droplet">
        <UserAvatar user={message.sender} size="small" />
      </span>
    ) : null;
  }

  render() {
    const { hovered } = this.state;

    return (
      <div
        className={cx('MessageItem', {
          'MessageItem-self': this.isAuthor(),
          'MessageItem-other': !this.isAuthor(),
          'MessageItem-hovered': hovered,
        })}>
        <div
          className="MessageItem-row"
          onMouseEnter={this.handleEnterHover}
          onMouseLeave={this.handleLeaveHover}>
          {this.renderHover()}
          <div className="MessageItem-container MessageItem-phoneCall">{this.renderBody()}</div>
          {this.renderDroplet()}
        </div>
      </div>
    );
  }
}

export default PhoneCallMessageItem;
